import { notificationsApi } from '../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// The server sends a `ping` event every 30 s; a stream silent for longer than this
// is dead (a laptop that slept, a network that changed) even if it still looks open.
export const SILENT_MS = 75_000;
const MAX_WAIT_MS = 30_000;

/**
 * Keeps the person's live stream (/api/notifications/stream) open for as long as it
 * is wanted: a failed connection, a backend restarting during a deploy or a stream
 * gone quiet is tried again, waiting longer each time (1 s up to 30 s). Events sent
 * while the stream was down are lost, so `onReconnect` runs on every connection
 * after the first and should refresh what is on screen.
 *
 * `onEvent` gets each parsed event except the handshake. Returns a function that
 * closes the stream for good.
 */
export function openLiveStream({ onEvent, onReconnect, getToken = () => localStorage.getItem('token') }) {
  let source = null;
  let timer = null;
  let watchdog = null;
  let stopped = false;
  let asking = false;  // waiting for a stream token
  let failures = 0;
  let connectedBefore = false;
  let lastSeen = Date.now();

  const drop = () => {
    clearInterval(watchdog);
    watchdog = null;
    if (source) {
      source.onmessage = null;
      source.onerror = null;
      source.close();
      source = null;
    }
  };

  const retry = () => {
    drop();
    if (stopped) return;
    clearTimeout(timer);
    timer = setTimeout(connect, Math.min(1000 * 2 ** failures, MAX_WAIT_MS));
    failures += 1;
  };

  const alive = () => { lastSeen = Date.now(); };

  async function connect() {
    timer = null;
    if (stopped || !getToken()) return;
    // A short-lived one-time token, so the sign-in token never shows up in URL logs.
    let token;
    asking = true;
    try {
      token = (await notificationsApi.getSseToken()).data.sse_token;
    } catch {
      asking = false;
      retry();
      return;
    }
    asking = false;
    if (stopped) return;
    alive();
    source = new EventSource(`${BACKEND_URL}/api/notifications/stream?token=${encodeURIComponent(token)}`);
    source.addEventListener('ping', alive);
    source.onmessage = (event) => {
      alive();
      let data;
      try { data = JSON.parse(event.data); } catch { return; }
      if (data?.type === 'connected') {
        failures = 0;
        if (connectedBefore) onReconnect?.();
        connectedBefore = true;
        return;
      }
      if (data) onEvent(data);
    };
    source.onerror = retry;
    watchdog = setInterval(() => {
      if (Date.now() - lastSeen > SILENT_MS) retry();
    }, 15_000);
  }

  // Back from sleep, from another app or onto a network: check at once, not at the
  // next watchdog tick.
  const wake = () => {
    if (stopped || document.visibilityState === 'hidden') return;
    if (!source && !timer && !asking) connect();
    else if (source && Date.now() - lastSeen > SILENT_MS) retry();
  };
  document.addEventListener('visibilitychange', wake);
  window.addEventListener('online', wake);

  connect();

  return () => {
    stopped = true;
    clearTimeout(timer);
    drop();
    document.removeEventListener('visibilitychange', wake);
    window.removeEventListener('online', wake);
  };
}
